import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { api, formatApiError } from '@/App';
import PdfPreviewDialog from '@/components/PdfPreviewDialog';
import DocumentBuilderDialog from '@/components/DocumentBuilderDialog';
import ConvertToClientDialog from '@/components/ConvertToClientDialog';
import { FileText, Upload, Download, PenNib, MagnifyingGlass, Receipt, Trash, Eye, Plus, PaperPlaneTilt, UserPlus } from '@phosphor-icons/react';

const QUOT_STATUS_COLORS = { draft: 'bg-gray-100 text-gray-700', pending: 'bg-yellow-100 text-yellow-700', accepted: 'bg-blue-100 text-blue-700', declined: 'bg-red-100 text-red-700', signed: 'bg-green-100 text-green-700' };
const INV_STATUS_COLORS = { draft: 'bg-gray-100 text-gray-700', sent: 'bg-blue-100 text-blue-700', unpaid: 'bg-yellow-100 text-yellow-700', partially_paid: 'bg-amber-100 text-amber-800', paid: 'bg-green-100 text-green-700', overdue: 'bg-red-100 text-red-700', cancelled: 'bg-gray-100 text-gray-400' };

const AdminQuotations = () => {
  const navigate = useNavigate();
  const [quotations, setQuotations] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [signedContracts, setSignedContracts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadType, setUploadType] = useState('quotation'); // quotation or invoice
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('quotations');
  const [uploadForm, setUploadForm] = useState({ company_id: '', case_id: '', description: '', amount: '', invoice_number: '' });
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null); // { url, title, filename }
  const [builderOpen, setBuilderOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [convertDoc, setConvertDoc] = useState(null);

  const fetchData = async () => {
    try {
      const [qRes, scRes, cRes, iRes, casesRes, setRes] = await Promise.all([
        api.get('/quotations'), api.get('/signed-contracts'), api.get('/companies'), api.get('/invoices'), api.get('/cases'), api.get('/invoicing/settings')
      ]);
      setSettings(setRes.data);
      setQuotations(qRes.data);
      setSignedContracts(scRes.data);
      setCompanies(cRes.data);
      setInvoices(iRes.data);
      setCases(casesRes.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const openUpload = (type) => {
    setUploadType(type);
    setUploadForm({ company_id: '', case_id: '', description: '', amount: '', invoice_number: '' });
    setUploadFile(null);
    setUploadOpen(true);
  };

  const handleUpload = async () => {
    if (!uploadFile || !uploadForm.company_id) { toast.error('Please select a company and file'); return; }
    if (uploadType === 'invoice' && !(Number(uploadForm.amount) > 0)) {
      toast.error('Enter the invoice amount — it is needed for statements and payment tracking');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      const params = new URLSearchParams({ company_id: uploadForm.company_id });
      if (uploadForm.case_id && uploadForm.case_id !== 'none') params.append('case_id', uploadForm.case_id);
      if (uploadForm.description) params.append('description', uploadForm.description);
      if (uploadForm.amount) params.append('amount', uploadForm.amount);
      if (uploadType === 'invoice' && uploadForm.invoice_number) params.append('invoice_number', uploadForm.invoice_number);

      const endpoint = uploadType === 'invoice' ? '/invoices' : '/quotations';
      await api.post(`${endpoint}?${params.toString()}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${uploadType === 'invoice' ? 'Invoice' : 'Quotation'} uploaded`);
      setUploadOpen(false);
      fetchData();
    } catch (e) { toast.error(formatApiError(e)); } finally { setUploading(false); }
  };

  const updateInvoiceStatus = async (invoiceId, status) => {
    try {
      await api.put(`/invoices/${invoiceId}?status=${status}`);
      toast.success(`Invoice marked as ${status}`);
      fetchData();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const invoiceFromQuotation = async (q) => {
    // Generated quotations carry line items, so convert them server-side
    if (q.source === 'generated') {
      setBusyId(q.quotation_id);
      try {
        const { data } = await api.post(`/quotations/${q.quotation_id}/convert-to-invoice`);
        toast.success(`Draft invoice ${data.invoice_number} created from ${q.quotation_number}`);
        navigate('/admin/invoices');
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setBusyId(null); }
      return;
    }
    const params = new URLSearchParams();
    if (q.company_id) params.set('company_id', q.company_id);
    if (q.case_id) params.set('case_id', q.case_id);
    if (q.amount) params.set('amount', String(q.amount));
    params.set('description', q.description || 'Consulting services as per quotation');
    params.set('quotation_id', q.quotation_id);
    navigate(`/admin/invoices?${params.toString()}`);
  };

  const sendQuotation = async (q) => {
    setBusyId(q.quotation_id);
    try {
      const { data } = await api.post(`/quotations/${q.quotation_id}/send`);
      data.email_sent ? toast.success(data.message) : toast.warning(data.message);
      fetchData();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyId(null); }
  };

  const handleDeleteQuotation = async (id) => {    if (!window.confirm('Delete this quotation?')) return;
    try { await api.delete(`/quotations/${id}`); toast.success('Deleted'); fetchData(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const handleDeleteInvoice = async (id) => {
    if (!window.confirm('Delete this invoice?')) return;
    try { await api.delete(`/invoices/${id}`); toast.success('Deleted'); fetchData(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const handleDeleteContract = async (id) => {
    if (!window.confirm('Delete this signed contract?')) return;
    try { await api.delete(`/signed-contracts/${id}`); toast.success('Deleted'); fetchData(); } catch (e) { toast.error(formatApiError(e)); }
  };

  const filtered = tab === 'quotations'
    ? quotations.filter(q => q.company_name?.toLowerCase().includes(search.toLowerCase()) || q.description?.toLowerCase().includes(search.toLowerCase()))
    : tab === 'invoices'
    ? invoices.filter(i => i.company_name?.toLowerCase().includes(search.toLowerCase()) || i.description?.toLowerCase().includes(search.toLowerCase()) || i.invoice_number?.toLowerCase().includes(search.toLowerCase()))
    : signedContracts.filter(c => c.company_name?.toLowerCase().includes(search.toLowerCase()));

  if (loading) return <AdminLayout title="Quotations"><div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-[#4CAF50] border-t-transparent rounded-full animate-spin"></div></div></AdminLayout>;

  return (
    <AdminLayout title="Quotations & Invoices">
      <div className="space-y-6 fade-in">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#0A192F]">Quotations, Invoices & Contracts</h1>
            <p className="text-sm text-gray-500 mt-1">Manage client documents</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => setBuilderOpen(true)} className="bg-[#4CAF50] hover:bg-[#45a049] text-white rounded-none" data-testid="new-quotation-btn">
              <Plus className="w-4 h-4 mr-2" /> New Quotation
            </Button>
            <Button onClick={() => openUpload('quotation')} variant="outline" className="rounded-none border-[#4CAF50] text-[#4CAF50] hover:bg-[#4CAF50]/10" data-testid="upload-quotation-btn">
              <Upload className="w-4 h-4 mr-2" /> Upload Quotation
            </Button>
            <Button onClick={() => openUpload('invoice')} variant="outline" className="rounded-none" data-testid="upload-invoice-btn">
              <Upload className="w-4 h-4 mr-2" /> Upload Invoice
            </Button>
          </div>
        </div>

        <div className="flex gap-1 border-b border-gray-200">
          <button onClick={() => setTab('quotations')} className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === 'quotations' ? 'border-[#4CAF50] text-[#4CAF50]' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-testid="tab-quotations">Quotations ({quotations.length})</button>
          <button onClick={() => setTab('invoices')} className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === 'invoices' ? 'border-[#4CAF50] text-[#4CAF50]' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-testid="tab-invoices">Invoices ({invoices.length})</button>
          <button onClick={() => setTab('signed')} className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === 'signed' ? 'border-[#4CAF50] text-[#4CAF50]' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-testid="tab-signed">Signed Contracts ({signedContracts.length})</button>
        </div>

        <div className="relative max-w-md">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 rounded-none" />
        </div>

        {/* Quotations Tab */}
        {tab === 'quotations' && (
          <div className="space-y-3">
            {filtered.map(q => (
              <Card key={q.quotation_id} data-testid={`quotation-${q.quotation_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <FileText className="w-8 h-8 text-[#4CAF50]" />
                      <div>
                        <p className="font-medium text-[#0A192F]">
                          {q.quotation_number ? `${q.quotation_number} — ` : ''}{q.company_name}
                        </p>
                        <p className="text-sm text-gray-500">{q.description || q.original_filename}</p>
                        <div className="flex items-center gap-2 mt-1">
                          {q.amount && <span className="text-sm font-medium">R {q.amount?.toLocaleString('en-ZA', { minimumFractionDigits: 2 })} <span className="text-xs font-normal text-gray-500">incl. VAT</span></span>}
                          <span className="text-xs text-gray-400">{new Date(q.created_at).toLocaleDateString()}</span>
                          {q.terms_accepted && <Badge className="bg-green-50 text-green-600 text-xs">T&C Accepted</Badge>}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge className={QUOT_STATUS_COLORS[q.status]}>{q.status}</Badge>
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-none border-[#4CAF50] text-[#4CAF50] hover:bg-[#4CAF50]/10"
                        disabled={busyId === q.quotation_id}
                        onClick={() => invoiceFromQuotation(q)}
                        data-testid={`invoice-from-quotation-${q.quotation_id}`}
                      >
                        <Receipt className="w-4 h-4 mr-1" /> Create Invoice
                      </Button>
                      {q.ad_hoc_client && (
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Turn into a full client with portal access"
                          onClick={() => setConvertDoc(q)}
                          data-testid={`convert-client-btn-${q.quotation_id}`}
                        >
                          <UserPlus className="w-4 h-4 text-[#0055FF]" />
                        </Button>
                      )}
                      {q.source === 'generated' && (
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Email to customer"
                          disabled={busyId === q.quotation_id}
                          onClick={() => sendQuotation(q)}
                          data-testid={`send-quotation-${q.quotation_id}`}
                        >
                          <PaperPlaneTilt className="w-4 h-4 text-[#0055FF]" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setPreview({
                          url: q.source === 'generated' ? `/api/quotations/${q.quotation_id}/pdf` : `/api/quotations/${q.quotation_id}/download`,
                          title: `Quotation — ${q.company_name}`,
                          filename: q.source === 'generated' ? `Quotation-${q.quotation_number}.pdf` : q.original_filename,
                        })}
                        data-testid={`view-quotation-${q.quotation_id}`}
                        aria-label="View quotation"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <a
                        href={q.source === 'generated' ? `/api/quotations/${q.quotation_id}/pdf?download=true` : `/api/quotations/${q.quotation_id}/download`}
                        target="_blank" rel="noopener noreferrer"
                      >
                        <Button variant="ghost" size="icon"><Download className="w-4 h-4" /></Button>
                      </a>
                      <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-700" onClick={() => handleDeleteQuotation(q.quotation_id)}><Trash className="w-4 h-4" /></Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            {filtered.length === 0 && <Card><CardContent className="p-12 text-center text-gray-500">No quotations uploaded yet.</CardContent></Card>}
          </div>
        )}

        {/* Invoices Tab */}
        {tab === 'invoices' && (
          <div className="space-y-3">
            {filtered.map(i => (
              <Card key={i.invoice_id} data-testid={`invoice-${i.invoice_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <Receipt className="w-8 h-8 text-[#3B82F6]" />
                      <div>
                        <p className="font-medium text-[#0A192F]">{i.company_name}</p>
                        <p className="text-sm text-gray-500">{i.invoice_number ? `#${i.invoice_number} — ` : ''}{i.description || i.original_filename}</p>
                        <div className="flex items-center gap-2 mt-1">
                          {i.amount && <span className="text-sm font-medium">R {i.amount?.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</span>}
                          <span className="text-xs text-gray-400">{new Date(i.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {i.source === 'generated' ? (
                        <Link to="/admin/invoices" className="text-xs text-[#0055FF] underline" data-testid={`manage-invoice-${i.invoice_id}`}>
                          Manage in Invoicing
                        </Link>
                      ) : (
                        <Select value={i.status} onValueChange={(v) => updateInvoiceStatus(i.invoice_id, v)}>
                          <SelectTrigger className="w-28 rounded-none h-8 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="unpaid">Unpaid</SelectItem>
                            <SelectItem value="paid">Paid</SelectItem>
                            <SelectItem value="overdue">Overdue</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                      <Badge className={INV_STATUS_COLORS[i.display_status || i.status] || 'bg-gray-100 text-gray-700'}>
                        {(i.display_status || i.status || '').replace('_', ' ')}
                      </Badge>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setPreview({
                          url: i.source === 'generated' ? `/api/invoices/${i.invoice_id}/pdf` : `/api/invoices/${i.invoice_id}/download`,
                          title: `Invoice — ${i.company_name}`,
                          filename: i.source === 'generated' ? `Invoice-${i.invoice_number}.pdf` : i.original_filename,
                        })}
                        data-testid={`view-invoice-${i.invoice_id}`}
                        aria-label="View invoice"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <a
                        href={i.source === 'generated' ? `/api/invoices/${i.invoice_id}/pdf?download=true` : `/api/invoices/${i.invoice_id}/download`}
                        target="_blank" rel="noopener noreferrer"
                      >
                        <Button variant="ghost" size="icon"><Download className="w-4 h-4" /></Button>
                      </a>
                      <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-700" onClick={() => handleDeleteInvoice(i.invoice_id)}><Trash className="w-4 h-4" /></Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            {filtered.length === 0 && <Card><CardContent className="p-12 text-center text-gray-500">No invoices uploaded yet.</CardContent></Card>}
          </div>
        )}

        {/* Signed Tab */}
        {tab === 'signed' && (
          <div className="space-y-3">
            {filtered.map(c => (
              <Card key={c.contract_doc_id} data-testid={`signed-${c.contract_doc_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <PenNib className="w-8 h-8 text-[#4CAF50]" />
                      <div>
                        <p className="font-medium text-[#0A192F]">{c.company_name}</p>
                        <p className="text-sm text-gray-500">Signed by: {c.signed_by_name} ({c.signed_by_email})</p>
                        <p className="text-xs text-gray-400 mt-1">Signed: {new Date(c.signature_timestamp).toLocaleString()} | IP: {c.ip_address}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge className="bg-green-100 text-green-700">Signed</Badge>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setPreview({ url: `/api/signed-contracts/${c.contract_doc_id}/download`, title: `Signed Contract — ${c.company_name}`, filename: `signed-${c.company_name}.pdf` })}
                        data-testid={`view-signed-${c.contract_doc_id}`}
                        aria-label="View signed contract"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <a href={`/api/signed-contracts/${c.contract_doc_id}/download`} target="_blank" rel="noopener noreferrer"><Button variant="ghost" size="icon"><Download className="w-4 h-4" /></Button></a>
                      <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-700" onClick={() => handleDeleteContract(c.contract_doc_id)}><Trash className="w-4 h-4" /></Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            {filtered.length === 0 && <Card><CardContent className="p-12 text-center text-gray-500">No signed contracts yet.</CardContent></Card>}
          </div>
        )}

        {/* Upload Dialog */}
        <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Upload {uploadType === 'invoice' ? 'Invoice' : 'Quotation'}</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>Company *</Label>
                <Select value={uploadForm.company_id} onValueChange={(v) => setUploadForm({ ...uploadForm, company_id: v })}>
                  <SelectTrigger className="rounded-none" data-testid="upload-company-select"><SelectValue placeholder="Select company..." /></SelectTrigger>
                  <SelectContent>{companies.map(c => <SelectItem key={c.company_id} value={c.company_id}>{c.company_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Link to Service Request</Label>
                <Select value={uploadForm.case_id} onValueChange={(v) => setUploadForm({ ...uploadForm, case_id: v })}>
                  <SelectTrigger className="rounded-none" data-testid="upload-case-select"><SelectValue placeholder="Select case (optional)..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No linked case</SelectItem>
                    {cases.filter(c => !uploadForm.company_id || c.company_id === uploadForm.company_id).map(c => (
                      <SelectItem key={c.case_id} value={c.case_id}>{c.case_id} — {c.service_type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {uploadType === 'invoice' && (
                <div>
                  <Label>Invoice Number</Label>
                  <Input value={uploadForm.invoice_number} onChange={(e) => setUploadForm({ ...uploadForm, invoice_number: e.target.value })} placeholder="INV-001" className="rounded-none" />
                </div>
              )}
              <div><Label>Description</Label><Input value={uploadForm.description} onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })} placeholder="e.g. Electricity audit" className="rounded-none" /></div>
              <div><Label>Amount (R, incl. VAT){uploadType === 'invoice' ? ' *' : ''}</Label><Input type="number" value={uploadForm.amount} onChange={(e) => setUploadForm({ ...uploadForm, amount: e.target.value })} placeholder="0.00" className="rounded-none" data-testid="upload-amount-input" /></div>
              <div><Label>{uploadType === 'invoice' ? 'Invoice' : 'Quotation'} PDF *</Label><Input type="file" accept=".pdf,.doc,.docx" onChange={(e) => setUploadFile(e.target.files?.[0])} className="rounded-none" data-testid="upload-file-input" /></div>
              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setUploadOpen(false)} className="rounded-none">Cancel</Button>
                <Button onClick={handleUpload} disabled={uploading} className="bg-[#4CAF50] hover:bg-[#45a049] text-white rounded-none" data-testid="submit-upload-btn">{uploading ? 'Uploading...' : 'Upload'}</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <ConvertToClientDialog
          open={!!convertDoc}
          onOpenChange={(open) => !open && setConvertDoc(null)}
          doc={convertDoc}
          onConverted={fetchData}
        />

        <DocumentBuilderDialog
          kind="quotation"
          open={builderOpen}
          onOpenChange={setBuilderOpen}
          companies={companies}
          cases={cases}
          settings={settings}
          onCreated={fetchData}
        />

        <PdfPreviewDialog
          open={!!preview}
          onOpenChange={(open) => !open && setPreview(null)}
          url={preview?.url}
          title={preview?.title}
          filename={preview?.filename}
        />
      </div>
    </AdminLayout>
  );
};

export default AdminQuotations;
